import { Download, Loader2, Play } from 'lucide-react'
import { type FormEvent, type ReactElement, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { config } from '@/config'
import { ApiService } from '@/service/api'
import { KgDebugApi } from './api'
import { downloadName, entrySummary, entryTitle, splitNames } from './summary'
import type { KgDebugEntry, KgDebugRequest, KgDebugRun } from './types'

const PRE =
  'bg-muted text-xs p-3 rounded font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto'
const LABEL =
  'text-[10px] font-medium uppercase tracking-wide text-muted-foreground'
const INPUT = 'w-full rounded border bg-background px-2 py-1 text-sm'

/**
 * `/kg-debug` — run the KG retrieval engine against the active org's KG and read everything it
 * did: each engine step and each model turn in order, with the prompt a turn was sent, the
 * planner line it wrote, the parse, each resolve and its candidates, every SQL read and its rows,
 * and the evidence the answer model saw.
 *
 * Two modes, the two `just kg-ask` has: a QUESTION runs the whole engine; a grammar LINE stands in
 * for the planner reply, so a line can be tried against the KG with no plan turn (and, ticked,
 * answered). `?q=`, `?line=` and `?sheet=` prefill the form.
 *
 * Reads `POST /v1/kg-retrieval/debug` (#34762), served only where the web tier runs with
 * `KG_RETRIEVAL_DEBUG=on`; elsewhere the run answers 404 and the page says so. Standalone engineer
 * page like `/kg-trace`: no nav, no sidebar. "Download JSON" saves exactly what the page shows.
 */
export default function KgDebugPage(): ReactElement {
  const [params] = useSearchParams()
  const [question, setQuestion] = useState<string>(params.get('q') ?? '')
  const [line, setLine] = useState<string>(params.get('line') ?? '')
  const [answerLine, setAnswerLine] = useState<boolean>(false)
  const [activeSheet, setActiveSheet] = useState<string>(
    params.get('sheet') ?? ''
  )
  const [sheetsInView, setSheetsInView] = useState<string>('')
  const [run, setRun] = useState<KgDebugRun | null>(null)
  const [selected, setSelected] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const api = useMemo(
    () => new KgDebugApi(new ApiService(config.apiBaseUrl)),
    []
  )
  const canRun = (question.trim() !== '' || line.trim() !== '') && !loading

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (!canRun) return
    setLoading(true)
    setError(null)
    const sheets = splitNames(sheetsInView)
    const body: KgDebugRequest = {
      trace: true,
      ...(question.trim() === '' ? {} : { question: question.trim() }),
      ...(line.trim() === '' ? {} : { line: line.trim(), answer: answerLine }),
      ...(activeSheet.trim() === '' ? {} : { sheet: activeSheet.trim() }),
      ...(sheets === undefined ? {} : { sheets_in_view: sheets }),
    }
    try {
      const result = await api.run(body)
      setRun(result)
      // Open on the step that went wrong when one did: that is the one to read first.
      const failed = result.timeline.findIndex(isFailed)
      setSelected(failed >= 0 ? failed : 0)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      setError(
        status === 404
          ? 'The debug endpoint is not enabled on this environment (KG_RETRIEVAL_DEBUG is off).'
          : err instanceof Error
            ? err.message
            : String(err)
      )
    } finally {
      setLoading(false)
    }
  }

  const download = (): void => {
    if (run === null) return
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(run, null, 2)], { type: 'application/json' })
    )
    const a = document.createElement('a')
    a.href = url
    a.download = downloadName(run.question, Date.now())
    a.click()
    URL.revokeObjectURL(url)
  }

  const entry = run?.timeline[selected]

  return (
    <div className="min-h-screen bg-background p-4 text-sm text-foreground">
      <h1 className="mb-3 text-lg font-semibold">KG query debugger</h1>
      <form
        onSubmit={(e) => void submit(e)}
        className="mb-4 grid gap-2 md:grid-cols-2"
      >
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className={LABEL}>Question</span>
          <textarea
            aria-label="Question"
            className={INPUT}
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What is ZHS-1016A?"
          />
        </label>
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className={LABEL}>
            Grammar line (optional: replaces the planner, no plan turn)
          </span>
          <input
            aria-label="Grammar line"
            className={`${INPUT} font-mono`}
            value={line}
            onChange={(e) => setLine(e.target.value)}
            placeholder='node("ZHS-1016A") -> *'
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={LABEL}>Sheet in view (optional)</span>
          <input
            aria-label="Sheet in view"
            className={INPUT}
            value={activeSheet}
            onChange={(e) => setActiveSheet(e.target.value)}
            placeholder="R279032009P04"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={LABEL}>
            Other sheets on the board (comma-separated)
          </span>
          <input
            aria-label="Sheets on the board"
            className={INPUT}
            value={sheetsInView}
            onChange={(e) => setSheetsInView(e.target.value)}
          />
        </label>
        <div className="flex items-center gap-2 md:col-span-2">
          <button
            type="submit"
            disabled={!canRun}
            className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1 text-primary-foreground disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {loading ? 'Running… (model turns take a while)' : 'Run'}
          </button>
          {line.trim() !== '' && (
            <label className="inline-flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={answerLine}
                onChange={(e) => setAnswerLine(e.target.checked)}
              />
              also run the answer turn on the line&apos;s evidence
            </label>
          )}
          {run !== null && (
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center gap-1 rounded border px-3 py-1"
            >
              <Download className="h-4 w-4" /> Download JSON
            </button>
          )}
        </div>
      </form>

      {error !== null && (
        <p role="alert" className="mb-4 text-red-700 dark:text-red-400">
          {error}
        </p>
      )}

      {run !== null && (
        <>
          <RunHeader run={run} />
          <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <ol aria-label="Timeline" className="flex flex-col gap-1">
              {run.timeline.map((e, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setSelected(i)}
                    className={`w-full rounded border px-2 py-1 text-left hover:bg-muted ${
                      i === selected ? 'border-primary bg-muted' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-6 text-muted-foreground">{i + 1}</span>
                      <span
                        className={`font-medium ${isFailed(e) ? 'text-red-700 dark:text-red-400' : ''}`}
                      >
                        {entryTitle(e)}
                      </span>
                      <span className="ml-auto font-mono text-xs text-muted-foreground">
                        {e.endedAt - e.startedAt} ms
                      </span>
                    </div>
                    <div className="truncate pl-8 text-xs text-muted-foreground">
                      {entrySummary(e)}
                    </div>
                  </button>
                </li>
              ))}
            </ol>
            {entry !== undefined && <EntryInspector entry={entry} />}
          </div>
        </>
      )}
    </div>
  )
}

function isFailed(e: KgDebugEntry): boolean {
  return e.kind === 'step' ? e.status === 'error' : e.error !== undefined
}

function RunHeader({ run }: { run: KgDebugRun }): ReactElement {
  const o = run.outcome
  const h = run.header
  const noEmbeddings = h.embedding_stamp === 'no_embeddings'
  return (
    <section className="mb-4 flex flex-col gap-1 rounded border p-3">
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span>
          outcome: <b className="text-foreground">{o.kind}</b>
        </span>
        <span>mode: {h.mode}</span>
        <span>workspace: {h.workspace.slug}</span>
        <span>model: {h.model}</span>
        <span>
          graph: {h.graph_engine}
          {h.graph_engine_reason ? ` (${h.graph_engine_reason})` : ''}
        </span>
        <span>understanding: {h.understanding_source}</span>
        <span>{run.timeline.length} entries</span>
        <span>{(h.duration_ms / 1000).toFixed(1)} s</span>
        <span className="font-mono">{h.correlation_id}</span>
      </div>
      {noEmbeddings && (
        <p className="text-amber-700 dark:text-amber-400">
          This KG has no entity embeddings: a name resolves only when the graph
          holds that spelling (exact or name match); the fuzzy fallback cannot
          run, so a near miss reads as &quot;no_embeddings&quot;.
        </p>
      )}
      {o.kind === 'answer' && (
        <>
          <p className="whitespace-pre-wrap">{o.text}</p>
          {o.query !== undefined && <code className="text-xs">{o.query}</code>}
        </>
      )}
      {o.kind === 'ambiguous' && (
        <p>
          “{o.span}” is ambiguous: {o.candidates?.length ?? 0} candidate(s). See
          the canonicalize step.
        </p>
      )}
      {o.kind === 'not_kg_answerable' && (
        <p className="text-amber-700 dark:text-amber-400">
          Declined{o.decline?.stage ? ` at ${o.decline.stage}` : ''}:{' '}
          {o.decline?.explanation ?? o.reason}
        </p>
      )}
    </section>
  )
}

function EntryInspector({ entry }: { entry: KgDebugEntry }): ReactElement {
  if (entry.kind === 'model') {
    return (
      <section aria-label="Entry detail" className="flex flex-col gap-3">
        <div className={LABEL}>
          model turn · {entry.purpose} · {entry.model}
        </div>
        <Block
          title={entry.error !== undefined ? 'Error' : 'Reply'}
          text={entry.error ?? entry.output ?? ''}
        />
        <Block title="User message" text={entry.input.user} />
        <Block title="System prompt" text={entry.input.system} />
      </section>
    )
  }
  const output = (
    typeof entry.output === 'object' && entry.output !== null
      ? entry.output
      : {}
  ) as Record<string, unknown>
  const rendered =
    typeof output.rendered === 'string' ? output.rendered : undefined
  const queries = Array.isArray(output.queries)
    ? (output.queries as Array<Record<string, unknown>>)
    : []
  return (
    <section aria-label="Entry detail" className="flex flex-col gap-3">
      <div className={LABEL}>
        step · {entry.stage}
        {entry.leg === undefined ? '' : ` · ${entry.leg}`} · {entry.status}
      </div>
      {queries.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className={LABEL}>SQL reads ({queries.length})</div>
          {queries.map((q, i) => (
            <pre key={i} className={PRE}>
              {typeof q.sql === 'string' ? q.sql : JSON.stringify(q, null, 2)}
              {'\n'}
              {/* query-log.ts: `rows` is the count, null when the executor never read them. */}
              {typeof q.rows === 'number'
                ? `→ ${q.rows} row(s)`
                : '→ rows not read'}
              {typeof q.ms === 'number' ? ` · ${q.ms} ms` : ''}
            </pre>
          ))}
        </div>
      )}
      {rendered !== undefined && (
        <Block title="Evidence sent to the answer model" text={rendered} />
      )}
      <Block
        title="Output"
        text={JSON.stringify(entry.output ?? null, null, 2)}
      />
      <Block
        title="Input"
        text={JSON.stringify(entry.input ?? null, null, 2)}
      />
    </section>
  )
}

function Block({ title, text }: { title: string; text: string }): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div className={LABEL}>{title}</div>
      <pre className={PRE}>{text}</pre>
    </div>
  )
}
